import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, LogIn, UserPlus, RotateCcw, AlertTriangle } from "lucide-react";
import { paths } from "app/paths";
import AnimatedSection from "components/AnimatedSection";
import StatusBadge from "components/admin/StatusBadge";
import CustomButton from "components/CustomButton";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import {
  verResumoNuvemshop,
  registrarCarrierNuvemshop,
  NuvemshopResumo,
} from "services/api";

type ViewState = "loading" | "ready" | "unauth" | "error";

function carrierStatus(carrier: NuvemshopResumo["carrier"]) {
  if (!carrier) return { tone: "slate" as const, label: "Não foi possível verificar" };
  if (!carrier.registered) return { tone: "yellow" as const, label: "Ainda não registrada" };
  if (carrier.active === false) return { tone: "yellow" as const, label: "Registrada, mas inativa" };
  if (carrier.active === true) return { tone: "green" as const, label: "Ativa" };
  return { tone: "slate" as const, label: "Registrada" };
}

function optionBadge(label: string, count: number | null | undefined) {
  const hasOptions = typeof count === "number" && count > 0;
  return (
    <StatusBadge
      label={hasOptions ? `${label}: ${count} opção${count === 1 ? "" : "ões"}` : `${label}: —`}
      tone={hasOptions ? "green" : "slate"}
    />
  );
}

export function NuvemshopConectado() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storeIdFromUrl = searchParams.get("store_id");

  const [viewState, setViewState] = useState<ViewState>("loading");
  const [resumo, setResumo] = useState<NuvemshopResumo | null>(null);
  const [registrando, setRegistrando] = useState(false);

  const carregarResumo = useCallback(async () => {
    setViewState("loading");
    try {
      const { data } = await verResumoNuvemshop();
      setResumo(data);
      setViewState("ready");
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setViewState("unauth");
      } else {
        setViewState("error");
      }
    }
  }, []);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  const handleRegistrarCarrier = async () => {
    setRegistrando(true);
    try {
      await registrarCarrierNuvemshop();
    } catch {
      // segue para o refresh mesmo assim — o resumo vai refletir o estado real
    } finally {
      setRegistrando(false);
      carregarResumo();
    }
  };

  const status = carrierStatus(resumo?.carrier ?? null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-4 py-10">
      <AnimatedSection
        animatedGradient={false}
        shine={false}
        className="w-full max-w-md text-center"
      >
        <img
          src="/images/intrex_logo.png"
          alt="Intrex"
          className="h-11 mx-auto mb-5"
        />

        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
          <CheckCircle2 className="h-8 w-8" />
        </div>

        <h2 className="text-xl font-bold text-slate-900 mb-1">Loja conectada!</h2>
        <p className="text-sm text-slate-500 mb-6">
          Sua loja Nuvemshop foi conectada com sucesso à Intrex.
        </p>

        {viewState === "loading" && (
          <div className="space-y-4">
            <Skeleton className="h-4 w-48 mx-auto" />
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="h-6 w-20 rounded-md" />
              <Skeleton className="h-6 w-28 rounded-md" />
              <Skeleton className="h-6 w-28 rounded-md" />
            </div>
            <Skeleton className="h-11 w-full rounded-lg mt-2" />
          </div>
        )}

        {viewState === "ready" && resumo?.connected && (
          <div>
            <p className="text-base font-semibold text-slate-800">
              {resumo.store?.name || `Loja #${resumo.storeId || storeIdFromUrl || ""}`}
            </p>
            {resumo.store?.email && (
              <p className="text-xs text-slate-400 mb-4">{resumo.store.email}</p>
            )}

            <div className="flex flex-wrap items-center justify-center gap-2 my-4">
              <StatusBadge label={`Frete internacional: ${status.label}`} tone={status.tone} />
              {resumo.carrier?.registered && (
                <>
                  {optionBadge("UPS", resumo.carrier.upsOptions)}
                  {optionBadge("FedEx", resumo.carrier.fedexOptions)}
                </>
              )}
            </div>

            {resumo.warnings && resumo.warnings.length > 0 && (
              <p className="text-xs text-slate-400 mb-4">
                Alguns dados não puderam ser carregados agora — isso não afeta sua conexão.
              </p>
            )}

            {resumo.carrier && !resumo.carrier.registered && (
              <button
                onClick={handleRegistrarCarrier}
                disabled={registrando}
                className="inline-flex items-center gap-1.5 text-xs text-brand-primary font-medium mb-4 hover:underline disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {registrando ? "Registrando..." : "Registrar transportadora novamente"}
              </button>
            )}

            <CustomButton
              label="Ir para o dashboard"
              className="w-full"
              onClick={() => navigate(paths.dashboard)}
            />
          </div>
        )}

        {viewState === "ready" && resumo && !resumo.connected && (
          <div>
            <p className="text-sm text-slate-500 mb-5">
              A conexão foi concluída, mas ainda não encontramos o vínculo dessa loja com a sua conta Intrex.
            </p>
            <div className="flex flex-col gap-2">
              <CustomButton
                label="Conectar novamente"
                className="w-full"
                onClick={() => navigate(paths.nuvemshopConectar)}
              />
              <Button
                variant="outline"
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => navigate(paths.dashboard)}
              >
                Ir para o dashboard
              </Button>
            </div>
          </div>
        )}

        {viewState === "unauth" && (
          <div>
            {storeIdFromUrl && (
              <p className="text-xs text-slate-400 mb-1">Store ID: {storeIdFromUrl}</p>
            )}
            <p className="text-sm text-slate-500 mb-5">
              Você ainda não está logado na Intrex neste navegador. Entre ou crie sua conta para acompanhar seus envios.
            </p>
            <div className="flex flex-col gap-2">
              <CustomButton
                label="Entrar"
                icon={<LogIn className="h-4 w-4" />}
                iconDirection="left"
                className="w-full"
                onClick={() => navigate(paths.login)}
              />
              <Button
                variant="outline"
                className="w-full gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => navigate("/cadastro")}
              >
                <UserPlus className="h-4 w-4" />
                Criar conta
              </Button>
            </div>
          </div>
        )}

        {viewState === "error" && (
          <div>
            <div className="flex items-center justify-center gap-1.5 text-amber-600 text-sm mb-5">
              <AlertTriangle className="h-4 w-4" />
              Não foi possível carregar os detalhes agora.
            </div>
            <div className="flex flex-col gap-2">
              <CustomButton
                label="Tentar novamente"
                className="w-full"
                onClick={carregarResumo}
              />
              <Button
                variant="outline"
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => navigate(paths.dashboard)}
              >
                Ir para o dashboard
              </Button>
            </div>
          </div>
        )}
      </AnimatedSection>
    </div>
  );
}
